/**
 * HomeGamePromoModal - one-time "Host a home game" nudge on Home.
 *
 * Shown once per player per device, the first time they land on Home with
 * nothing else on screen (no tour, no daily bonus, wheel or pack modal).
 * Players who already own a table never see it.
 *
 * The reward it promises is the existing referral bonus: a friend who signs
 * up from the player's link gets 5 PSS Coins and so does the player
 * (/creditReferralBonus). Table invite links carry the host's referral code
 * too, so inviting from the table counts. Agents earn revenue share instead
 * of the per-signup bonus, so their copy only promises the friend's 5.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
  VStack, HStack, Box, Text, Button, useToast,
} from '@chakra-ui/react';
import { Home, Link2, UserPlus } from 'lucide-react';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

const SEEN_KEY = (uid: string) => `pss:homeGamePromo:v1:${uid}`;
const FIRST_DELAY_MS = 2500;
const POLL_MS = 1500;
const GIVE_UP_MS = 3 * 60 * 1000;
const REWARD = 5;

function alreadySeen(uid: string): boolean {
  try { return !!localStorage.getItem(SEEN_KEY(uid)); } catch { return false; }
}
function markSeen(uid: string) {
  try { localStorage.setItem(SEEN_KEY(uid), String(Date.now())); } catch { /* private mode */ }
}
// Nothing else is competing for the screen: no guided tour and no other modal.
function screenIsFree(): boolean {
  if (document.body.dataset.pssTourActive === '1') return false;
  if (document.querySelector('.chakra-modal__content')) return false;
  return document.visibilityState === 'visible';
}

const Step: React.FC<{ n: number; title: string; body: string }> = ({ n, title, body }) => (
  <HStack align="flex-start" spacing={3} w="100%">
    <Box
      flexShrink={0} w="28px" h="28px" borderRadius="full"
      bg="rgba(0,217,217,0.15)" border="1px solid rgba(0,217,217,0.6)"
      color="cyan.300" fontWeight="800" fontSize="sm"
      display="flex" alignItems="center" justifyContent="center"
    >
      {n}
    </Box>
    <Box minW={0}>
      <Text color="white" fontWeight="700" fontSize="sm" lineHeight="1.3">{title}</Text>
      <Text color="gray.400" fontSize="xs" lineHeight="1.4">{body}</Text>
    </Box>
  </HStack>
);

const HomeGamePromoModal: React.FC<{ uid: string | null | undefined }> = ({ uid }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [isAgent, setIsAgent] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!uid || startedRef.current || alreadySeen(uid)) return;
    startedRef.current = true;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    (async () => {
      try {
        // Hosts already know the flow; don't nag them.
        const owned = await getDocs(query(collection(db, 'tables'), where('ownerId', '==', uid), limit(1)));
        if (!owned.empty) { markSeen(uid); return; }
      } catch { /* can't tell - show it anyway */ }
      try {
        const [userSnap, agentSnap] = await Promise.all([
          getDoc(doc(db, 'users', uid)),
          getDoc(doc(db, 'agents', uid)).catch(() => null),
        ]);
        const code = userSnap.exists() ? (userSnap.data() as any).referralCode : null;
        if (!cancelled) {
          setReferralCode(typeof code === 'string' && code ? code : null);
          setIsAgent(!!(agentSnap && agentSnap.exists() && (agentSnap.data() as any)?.active === true));
        }
      } catch { /* the table path still works without the code */ }

      const tryOpen = () => {
        if (cancelled) return;
        if (alreadySeen(uid) || Date.now() - startedAt > GIVE_UP_MS) return;
        if (screenIsFree()) { markSeen(uid); setOpen(true); return; }
        timer = setTimeout(tryOpen, POLL_MS);
      };
      timer = setTimeout(tryOpen, FIRST_DELAY_MS);
    })();

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [uid]);

  const close = () => setOpen(false);

  const startHomeGame = () => {
    close();
    navigate('/tables/create?mode=home&host=1');
  };

  const shareLink = async () => {
    if (!referralCode) { close(); navigate('/referrals'); return; }
    const url = `https://pokersweepstars.web.app/login?ref=${encodeURIComponent(referralCode)}`;
    const text = `Come play poker with me on PokerSweepStars. Sign up from my link and we each get ${REWARD} free PSS Coins.`;
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: 'PokerSweepStars', text, url });
        close();
        return;
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return; // closed the share sheet
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast({ title: 'Invite link copied', description: 'Paste it in a text or your group chat.', status: 'success', duration: 3000, position: 'top' });
      close();
    } catch {
      close();
      navigate('/referrals');
    }
  };

  const rewardLine = isAgent
    ? `Friends who sign up from your link get ${REWARD} free PSS Coins.`
    : `Every friend who signs up from your link: you each get ${REWARD} free PSS Coins.`;

  return (
    <Modal isOpen={open} onClose={close} isCentered size="sm" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent
        mx={4}
        bg="linear-gradient(160deg, #0d2838 0%, #0a1420 100%)"
        border="2px solid rgba(0,217,217,0.55)"
        borderRadius="2xl"
        boxShadow="0 0 48px rgba(0,217,217,0.25)"
      >
        <ModalCloseButton color="gray.400" />
        <ModalBody px={{ base: 5, md: 6 }} pt={7} pb={5}>
          <VStack spacing={4} align="stretch">
            <VStack spacing={2}>
              <Box
                w="56px" h="56px" borderRadius="full"
                bg="rgba(0,217,217,0.12)" border="1px solid rgba(0,217,217,0.5)"
                display="flex" alignItems="center" justifyContent="center"
              >
                <Home size={28} color="#22d3ee" />
              </Box>
              <Text color="white" fontSize="xl" fontWeight="800" textAlign="center" lineHeight="1.2">
                Host a home game
              </Text>
              <Text color="gray.300" fontSize="sm" textAlign="center">
                Poker's better with your crew. Open a private table and send your friends the link.
              </Text>
            </VStack>

            <Box
              bg="rgba(250,204,21,0.10)" border="1px solid rgba(250,204,21,0.45)"
              borderRadius="lg" px={3} py={2}
            >
              <Text color="yellow.300" fontWeight="800" fontSize="sm" textAlign="center">
                {rewardLine}
              </Text>
            </Box>

            <VStack spacing={3} align="stretch">
              <Step n={1} title="Start a private table" body="Free to host. Your table, your blinds, your people." />
              <Step n={2} title="Send your invite link" body="Text it or drop it in the group chat. It opens right to your table." />
              <Step n={3} title="Friends sign up and sit down" body={isAgent ? `They get ${REWARD} free PSS Coins the moment they sign up.` : `You each get ${REWARD} free PSS Coins the moment they sign up.`} />
            </VStack>

            <VStack spacing={2} pt={1}>
              <Button
                w="100%" h="50px" colorScheme="cyan" fontWeight="800"
                leftIcon={<UserPlus size={18} />}
                onClick={startHomeGame}
              >
                Start my home game
              </Button>
              <Button
                w="100%" h="44px" variant="outline" colorScheme="cyan"
                leftIcon={<Link2 size={16} />}
                onClick={shareLink}
              >
                Just share my invite link
              </Button>
              <Button variant="ghost" size="sm" color="gray.400" onClick={close}>
                Maybe later
              </Button>
            </VStack>

            <Text color="gray.500" fontSize="2xs" textAlign="center" lineHeight="1.4">
              Bonus applies to new players who sign up from your link. PSS Coins are usable in eligible states under the Official Sweepstakes Rules.
            </Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default HomeGamePromoModal;
